import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PropostaService } from "../services/fornecedor/proposta.service";

const usePropostasComprador = (user, ordcom, dataIni, dataFim, paged = true, page = 1, limit = 5, enabled = false) => {
  const query = useQuery({
    queryKey: ["propostas", ordcom, dataIni, dataFim, paged, page, limit],
    queryFn: async () => {
      let data = null; 

      if (ordcom > 0 ) {
        data = await PropostaService.getPropostas(user.token, dataIni, dataFim, ordcom, paged, page, limit); 
      } else {
        data = await PropostaService.getPropostasAgrupado(user.token, dataIni, dataFim, paged, page, limit);
      }

      return data;
    },
    staleTime: 5000,
    refetchInterval: 10000,
    enabled
  });

  return { ...query };
}

const usePropostasFornecedor = (user, codCli, ordcom, dataIni, dataFim, paged = true, page = 1, limit = 5, enabled = false) => {
  const query = useQuery({
    queryKey: ["propostas", codCli, ordcom, dataIni, dataFim, paged, page, limit],
    queryFn: async () => {
      let data = null;
      
      if (ordcom > 0) {
        data = await PropostaService.getPropostasByCodCli(user.token, codCli, ordcom, dataIni, dataFim, paged, page, limit);
      } else {
        data = await PropostaService.getPropostasAgrupadoByCodCli(user.token, codCli, dataIni, dataFim, paged, page, limit);
      }

      return data;
    },
    refetchInterval: false,
    enabled
  });

  return { ...query };
};

const useFiltrarPropostasFornecedor = (user, filtros, page = 1, limit = 5) => {
  const query = useQuery({
    queryKey: ["propostas-filtradas", filtros, page, limit],
    queryFn: async () => {
      const response = await PropostaService.filtrarPropostas(
        user.token,
        filtros,
        true,
        page,
        limit
      );
      return response;
    },
    retry: false,
    keepPreviousData: false,
    staleTime: 5000,
    refetchOnWindowFocus: false,
  });

  return { ...query };
};

const useResponderProposta = (user) => {
  const queryClient = useQueryClient();

  const respostaMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await PropostaService.responderProposta(user.token, payload);
      return response;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(["propostas-filtradas"]),
        queryClient.invalidateQueries(["respostas"])
      ]);
    },
    onError: async (error) => {
      const errorMessage = error.response?.data?.erro || "Erro desconhecido";
      return errorMessage;
    },
  });

  return {
    responderProposta: respostaMutation.mutate,
    responderPropostaAsync: respostaMutation.mutateAsync,
    isLoading: respostaMutation.isPending,
    isError: respostaMutation.isError,
    isSuccess: respostaMutation.isSuccess,
    error: respostaMutation.error,
    data: respostaMutation.data,
  };
};

const useRespostasFornecedor = (user, arq281) => {
  const query = useQuery({
    queryKey: ["respostas", arq281],
    queryFn: async () => {
      const data = await PropostaService.getRespostasByArq281(user.token, arq281);
      return data;
    },
    keepPreviousData: true,
    staleTime: 30000,
    gcTime: 1000 * 60 * 10,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!arq281,
  });

  return { ...query };
}

export { usePropostasComprador, usePropostasFornecedor, useFiltrarPropostasFornecedor, useResponderProposta, useRespostasFornecedor };
